import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import AuthShell from "../components/AuthShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start monitoring your websites in under a minute."
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700" data-testid="go-login-link">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="space-y-1.5">
          <Label htmlFor="name" className="text-xs font-semibold">Name</Label>
          <Input
            id="name"
            data-testid="register-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
            required
            className="focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs font-semibold">Email</Label>
          <Input
            id="email"
            type="email"
            data-testid="register-email-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            className="focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password" className="text-xs font-semibold">Password</Label>
          <Input
            id="password"
            type="password"
            data-testid="register-password-input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 chars, 1 letter & 1 number"
            required
            className="focus:ring-2 focus:ring-blue-500"
          />
        </div>
        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="register-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={loading}
          data-testid="register-submit-btn"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
        >
          {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
          Create account
        </Button>
      </form>
    </AuthShell>
  );
}
